import { useState, useEffect, useCallback } from "react";
import { Search, Plus, Minus, Trash2, Package, X, Barcode, MapPin, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManualPickingListDialog({ open, onOpenChange, onCreated }) {
  const [listName, setListName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(searchQuery.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const performSearch = async (query) => {
    setIsSearching(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/inventory`, {
        params: { search: query, limit: 20 }
      });
      
      // API returns grouped data with total_antal and locations
      const results = response.data.items.map(item => ({
        ean: item.ean,
        artikelnummer: item.artikelnummer,
        produktnamn: item.produktnamn,
        tillverkare: item.tillverkare,
        kategori: item.kategori,
        locations: item.locations.map(loc => ({
          location: loc.lagerplats,
          quantity: loc.antal
        })),
        totalStock: item.total_antal || 0
      }));

      setSearchResults(results);
    } catch (err) {
      console.error("Search error:", err);
      setError("Kunde inte söka efter produkter");
    } finally {
      setIsSearching(false);
    }
  };

  const addProduct = (product) => {
    // Validate order number before adding
    if (!orderNumber.trim()) {
      setError("Ange ordernummer innan du lägger till produkten");
      return;
    }
    
    // Check if already in list with same order number
    const existingKey = `${product.ean}-${orderNumber.trim()}`;
    const existing = selectedProducts.find(p => `${p.ean}-${p.orderNumber}` === existingKey);
    
    if (existing) {
      // Increase quantity for same product + order combination
      setSelectedProducts(prev => 
        prev.map(p => 
          `${p.ean}-${p.orderNumber}` === existingKey
            ? { ...p, quantity: p.quantity + 1 }
            : p
        )
      );
      toast.success(`Ökade antal för ${product.produktnamn} (Order: ${orderNumber.trim()})`);
    } else {
      // Add new product with order number
      setSelectedProducts(prev => [...prev, { 
        ...product, 
        quantity: 1, 
        orderNumber: orderNumber.trim() 
      }]);
      toast.success(`La till ${product.produktnamn} (Order: ${orderNumber.trim()})`);
    }
    
    // Clear search and order number for next product
    setSearchQuery("");
    setSearchResults([]);
    setOrderNumber("");
    setError(null);
  };

  const updateQuantity = (ean, orderNum, delta) => {
    setSelectedProducts(prev =>
      prev.map(p => {
        if (p.ean === ean && p.orderNumber === orderNum) {
          const newQty = Math.max(1, p.quantity + delta);
          return { ...p, quantity: newQty };
        }
        return p;
      })
    );
  };

  const removeProduct = (ean, orderNum) => {
    setSelectedProducts(prev => prev.filter(p => !(p.ean === ean && p.orderNumber === orderNum)));
  };

  const handleCreate = async () => {
    if (selectedProducts.length === 0) {
      setError("Lägg till minst en produkt");
      return;
    }

    // Check that all products have order numbers
    const missingOrder = selectedProducts.find(p => !p.orderNumber);
    if (missingOrder) {
      setError("Alla produkter måste ha ett ordernummer");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const name = listName.trim() || `Manuell plocklista ${new Date().toLocaleDateString('sv-SE')}`;
      
      const payload = {
        name,
        items: selectedProducts.map(p => ({
          ean: p.ean,
          quantity: p.quantity,
          order_number: p.orderNumber
        }))
      };

      const response = await axios.post(`${API}/picking/create-manual-picking-list`, payload);
      
      toast.success(`Plocklista skapad med ${response.data.total_items} produkter`);
      
      // Reset state
      setListName("");
      setOrderNumber("");
      setSearchQuery("");
      setSearchResults([]);
      setSelectedProducts([]);
      
      onOpenChange(false);
      
      if (onCreated) {
        onCreated(response.data.id);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Kunde inte skapa plocklistan";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setListName("");
    setOrderNumber("");
    setSearchQuery("");
    setSearchResults([]);
    setSelectedProducts([]);
    setError(null);
    onOpenChange(false);
  };

  const totalItems = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="manual-picking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Skapa manuell plocklista
          </DialogTitle>
          <DialogDescription>
            Sök efter produkter och lägg till dem i din plocklista
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* List Name */}
          <div className="space-y-2">
            <Label htmlFor="manual-list-name">Namn på plocklista</Label>
            <Input
              id="manual-list-name"
              placeholder="T.ex. Morgonplockning 2024-02-20"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              className="w-full"
              data-testid="manual-list-name-input"
            />
          </div>

          {/* Order Number and Product Search */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="order-number" className="flex items-center gap-1">
                Ordernummer för produkt <span className="text-destructive">*</span>
              </Label>
              <Input
                id="order-number"
                placeholder="T.ex. SO-2024-001"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                data-testid="order-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-search">Sök produkt (EAN eller artikelnummer)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="product-search"
                  placeholder="Skriv EAN eller artikelnummer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="product-search-input"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Card className="border-primary/30">
              <ScrollArea className="max-h-48">
                <CardContent className="p-2 space-y-1">
                  {searchResults.map((product) => (
                    <div
                      key={product.ean}
                      className="p-2 rounded-md hover:bg-muted cursor-pointer flex items-center justify-between group"
                      onClick={() => addProduct(product)}
                      data-testid={`search-result-${product.ean}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.produktnamn}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="font-mono">{product.artikelnummer}</span>
                          <span className="font-mono text-xs">EAN: {product.ean}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-1">
                          <Badge variant={product.totalStock > 0 ? "default" : "destructive"} className="text-xs">
                            {product.totalStock} st i lager
                          </Badge>
                          {product.totalStock > 0 && product.locations.length > 0 && (
                            <span className="text-muted-foreground">
                              {product.locations.map(l => l.location).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </ScrollArea>
            </Card>
          )}

          {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">
              Inga produkter hittades
            </p>
          )}

          {/* Selected Products */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <Label>Valda produkter</Label>
              {selectedProducts.length > 0 && (
                <Badge variant="outline">
                  {selectedProducts.length} rader, {totalItems} st totalt
                </Badge>
              )}
            </div>
            
            {selectedProducts.length === 0 ? (
              <Card className="flex items-center justify-center border-dashed min-h-[120px]">
                <div className="text-center p-6 text-muted-foreground">
                  <Barcode className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Inga produkter tillagda ännu</p>
                  <p className="text-sm">Ange ordernummer, sök produkt och klicka för att lägga till</p>
                </div>
              </Card>
            ) : (
              <div 
                className="border rounded-md"
                style={{ 
                  maxHeight: '280px', 
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'hsl(var(--primary)) transparent'
                }}
              >
                <div className="p-2 space-y-2">
                  {selectedProducts.map((product, index) => (
                    <Card key={`${product.ean}-${product.orderNumber}-${index}`} className="border-border" data-testid={`selected-product-${product.ean}-${product.orderNumber}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-medium">{product.produktnamn}</p>
                              <Badge variant="secondary" className="text-xs">
                                Order: {product.orderNumber}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="font-mono">{product.artikelnummer}</span>
                              <span className="font-mono text-xs">EAN: {product.ean}</span>
                            </div>
                            {product.totalStock > 0 && product.locations.length > 0 && (
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <MapPin className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                  {product.locations.map(l => `${l.location} (${l.quantity}st)`).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-1 bg-muted rounded-md">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9"
                                onClick={() => updateQuantity(product.ean, product.orderNumber, -1)}
                                disabled={product.quantity <= 1}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-8 text-center font-mono font-bold">
                                {product.quantity}
                              </span>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9"
                                onClick={() => updateQuantity(product.ean, product.orderNumber, 1)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            {/* Remove Button */}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-destructive hover:text-destructive"
                              onClick={() => removeProduct(product.ean, product.orderNumber)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm" data-testid="error-message">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} data-testid="cancel-btn">
            Avbryt
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={isCreating || selectedProducts.length === 0}
            className="gap-2"
            data-testid="create-manual-list-btn"
          >
            {isCreating ? (
              "Skapar..."
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Skapa plocklista ({totalItems} st)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
